import { useMemo, useRef } from 'react';
import { useBestFitContextMenu } from './useBestFitContextMenu';

const FOREIGN_BROKERS = [
  "AG", "AH", "AI", "AK", "BK", "BQ", "CG", "CS", "DP", "DR", "DU", "FS", "GW", "HD", "KK",
  "KZ", "LH", "LG", "LS", "MS", "RB", "RX", "TX", "YP", "YU", "ZP"
];
const GOVERNMENT_BROKERS = ['CC', 'NI', 'OD', 'DX'];

export const getBrokerColorClass = (brokerCode: string) => {
  if (!brokerCode || brokerCode === '--') return 'text-gray-500';
  if (GOVERNMENT_BROKERS.includes(brokerCode)) return 'text-[#10B981] font-semibold';
  if (FOREIGN_BROKERS.includes(brokerCode)) return 'text-[#EF4444] font-semibold';
  return 'text-white font-semibold';
};interface OrderBookRow {
  bidFreq:     number;
  bidBroker:   string;
  bidDelta:    number;
  bidLot:      number;
  bidPrice:    number;
  offerPrice:  number;
  offerLot:    number;
  offerDelta:  number;
  offerBroker: string;
  offerFreq:   number;
  ghost:       boolean; // true = recently departed, show faded
}

interface ActiveOrder {
  price:    number;
  openQty:  number;
  buySell:  string;
  broker:   string;
}

interface PriceChange {
  delta:  number;
  broker: string;
}

export function OrderBook({ currentTime, allOrders, prevClose, showBroker = true, onBidLotClick, onOfferLotClick, isFullDataLoaded = true }: { currentTime: number; allOrders: any[]; prevClose?: number | null; showBroker?: boolean; onBidLotClick?: (price: number) => void; onOfferLotClick?: (price: number) => void; isFullDataLoaded?: boolean }) {
  const stateRef = useRef<{
    lastTime: number; lastIndex: number; lastOrders: any[] | null;
    activeOrders: Map<string, ActiveOrder>;
    activeCounts: Map<string, number>;
    priceChanges: Map<string, PriceChange>;
    ghosts: Map<string, PriceChange & { price: number; buySell: string }>;
  }>({
    lastTime: -1, lastIndex: 0, lastOrders: null,
    activeOrders: new Map(),
    activeCounts: new Map(),
    priceChanges: new Map(),
    ghosts: new Map(),
  });

  const rows: OrderBookRow[] = useMemo(() => {
    if (!allOrders.length) {
      stateRef.current = {
        lastTime: -1, lastIndex: 0, lastOrders: allOrders,
        activeOrders: new Map(), activeCounts: new Map(), priceChanges: new Map(), ghosts: new Map(),
      };
      return [];
    }

    // Reset when a new dataset is loaded
    if (stateRef.current.lastOrders !== allOrders) {
      stateRef.current = {
        lastTime: -1, lastIndex: 0, lastOrders: allOrders,
        activeOrders: new Map(), activeCounts: new Map(), priceChanges: new Map(), ghosts: new Map(),
      };
    }

    const state = stateRef.current;

    // Guard for hot-reload
    if (!state.priceChanges) {
      state.lastIndex = 0; state.lastTime = -1;
      state.activeOrders = new Map(); state.activeCounts = new Map(); state.priceChanges = new Map(); state.ghosts = new Map();
    }
    if (!state.activeCounts) state.activeCounts = new Map();
    if (!state.ghosts) state.ghosts = new Map();

    if (currentTime < state.lastTime) {
      state.lastIndex = 0;
      state.activeOrders.clear();
      state.activeCounts.clear();
      state.priceChanges.clear();
      state.ghosts.clear();
    } else if (currentTime > state.lastTime && state.lastTime >= 0) {
      // Clear ghosts from previous tick when moving forward
      state.ghosts.clear();
    }

    const { activeOrders, activeCounts, priceChanges, ghosts } = state;

    const applyChange = (buySell: string, price: number, delta: number, broker: string) => {
      priceChanges.set(`${buySell}_${price}`, { delta, broker });
    };

    const incrementCount = (buySell: string, price: number) => {
      const key = `${buySell}_${price}`;
      activeCounts.set(key, (activeCounts.get(key) || 0) + 1);
    };

    const decrementCount = (buySell: string, price: number) => {
      const key = `${buySell}_${price}`;
      const current = activeCounts.get(key) || 0;
      if (current <= 1) {
        activeCounts.delete(key);
      } else {
        activeCounts.set(key, current - 1);
      }
    };

    const wouldBeEmpty = (buySell: string, price: number) => {
      const key = `${buySell}_${price}`;
      return (activeCounts.get(key) || 0) <= 1;
    };

    let i = state.lastIndex;
    for (; i < allOrders.length; i++) {
      const order = allOrders[i];
      if (order.timeValue > currentTime) break;

      const msg    = order.message;
      const broker = String(order.brokerCode || '--');

      if (msg === 'Open') {
        activeOrders.set(order.orderNo, {
          price: order.price, openQty: order.open, buySell: order.buySell, broker,
        });
        incrementCount(order.buySell, order.price);
        applyChange(order.buySell, order.price, +order.open, broker);

      } else if (msg === 'Amended' || msg === 'Amended Match') {
        const entry = activeOrders.get(order.orderNo);
        if (entry) {
          const useBroker = entry.broker !== '--' ? entry.broker : broker;
          if (entry.price !== order.price) {
            // Price changed: remove from old, add to new
            if (wouldBeEmpty(entry.buySell, entry.price)) {
              ghosts.set(`${entry.buySell}_${entry.price}`, {
                delta: -entry.openQty, broker: useBroker, price: entry.price, buySell: entry.buySell,
              });
            }
            decrementCount(entry.buySell, entry.price);
            incrementCount(order.buySell, order.price);
            applyChange(entry.buySell, entry.price, -entry.openQty, useBroker);
            applyChange(order.buySell, order.price, +order.open, useBroker);
          } else {
            applyChange(order.buySell, order.price, order.open - entry.openQty, useBroker);
          }
          entry.price = order.price; entry.openQty = order.open; entry.broker = useBroker;
        } else {
          activeOrders.set(order.orderNo, {
            price: order.price, openQty: order.open, buySell: order.buySell, broker,
          });
          incrementCount(order.buySell, order.price);
          applyChange(order.buySell, order.price, +order.open, broker);
        }

      } else if (msg === 'Withdrawn' || msg === 'Withdrawn Match') {
        const entry = activeOrders.get(order.orderNo);
        if (entry) {
          const useBroker = entry.broker !== '--' ? entry.broker : broker;
          applyChange(entry.buySell, entry.price, -entry.openQty, useBroker);
          if (wouldBeEmpty(entry.buySell, entry.price)) {
            ghosts.set(`${entry.buySell}_${entry.price}`, {
              delta: -entry.openQty, broker: useBroker, price: entry.price, buySell: entry.buySell,
            });
          }
          decrementCount(entry.buySell, entry.price);
          activeOrders.delete(order.orderNo);
        }

      } else if (msg === 'Full Matched') {
        const entry = activeOrders.get(order.orderNo);
        if (entry) {
          const useBroker = entry.broker !== '--' ? entry.broker : broker;
          applyChange(entry.buySell, entry.price, -entry.openQty, useBroker);
          if (wouldBeEmpty(entry.buySell, entry.price)) {
            ghosts.set(`${entry.buySell}_${entry.price}`, {
              delta: -entry.openQty, broker: useBroker, price: entry.price, buySell: entry.buySell,
            });
          }
          decrementCount(entry.buySell, entry.price);
          activeOrders.delete(order.orderNo);
        }

      } else if (msg === 'Partial Matched') {
        const entry = activeOrders.get(order.orderNo);
        if (entry) {
          const useBroker = entry.broker !== '--' ? entry.broker : broker;
          applyChange(entry.buySell, entry.price, order.open - entry.openQty, useBroker);
          if (order.open <= 0) {
            // open=0 means fully consumed despite being labelled Partial Matched
            if (wouldBeEmpty(entry.buySell, entry.price)) {
              ghosts.set(`${entry.buySell}_${entry.price}`, {
                delta: -entry.openQty, broker: useBroker, price: entry.price, buySell: entry.buySell,
              });
            }
            decrementCount(entry.buySell, entry.price);
            activeOrders.delete(order.orderNo);
          } else {
            entry.openQty = order.open;
          }
        } else {
          if (order.open > 0) {
            activeOrders.set(order.orderNo, {
              price: order.price, openQty: order.open, buySell: order.buySell, broker,
            });
            incrementCount(order.buySell, order.price);
            applyChange(order.buySell, order.price, +order.open, broker);
          }
        }
      }
    }

    state.lastIndex = i;
    state.lastTime  = currentTime;

    const bidMap   = new Map<number, { openQty: number; freq: number }>();
    const offerMap = new Map<number, { openQty: number; freq: number }>();

    // openQty (BLot/SLot) from incremental activeOrders state
    activeOrders.forEach(order => {
      if (order.openQty <= 0) return;
      const map = order.buySell === 'B' ? bidMap : offerMap;
      const ex  = map.get(order.price);
      if (ex) { ex.openQty += order.openQty; ex.freq += 1; }
      else map.set(order.price, { openQty: order.openQty, freq: 1 });
    });

    // Freq = count of rows shown in Buy/Sell Orders panels (Open + Partial Matched with open>0).
    // Computed via the same scan-based approach as BuyOrders/SellOrders visibleData
    // to guarantee perfect consistency regardless of incremental state drift.
    const bidFreqMap   = new Map<number, number>();
    const offerFreqMap = new Map<number, number>();
    {
      // Track last-known state per orderNo (identical to BuyOrders/SellOrders logic)
      const latestOpen = new Map<string, number>();
      const latestMsg  = new Map<string, string>();
      const latestPrice = new Map<string, number>();
      const latestSide  = new Map<string, string>();

      for (let j = 0; j < allOrders.length; j++) {
        const o = allOrders[j];
        if (o.timeValue > currentTime) break;

        let open = o.open as number;
        let msg  = o.message as string;

        // Mirror BuyOrders/SellOrders normalization
        if (msg === 'Withdrawn' || msg === 'Withdrawn Match' || msg === 'Full Matched') {
          open = 0;
        }
        if (msg === 'Partial Matched' && open <= 0) {
          open = 0;
          msg  = 'Full Matched';
        }

        latestMsg.set(o.orderNo, msg);
        latestOpen.set(o.orderNo, open);
        latestPrice.set(o.orderNo, o.price);
        latestSide.set(o.orderNo, o.buySell);
      }

      for (const orderNo of latestMsg.keys()) {
        const open  = latestOpen.get(orderNo) ?? 0;
        const msg   = latestMsg.get(orderNo)!;
        const price = latestPrice.get(orderNo) ?? 0;
        const side  = latestSide.get(orderNo)!;

        if (open <= 0) continue;
        if (msg !== 'Open' && msg !== 'Partial Matched') continue;
        if (!price) continue;

        if (side === 'B') bidFreqMap.set(price, (bidFreqMap.get(price) ?? 0) + 1);
        else offerFreqMap.set(price, (offerFreqMap.get(price) ?? 0) + 1);
      }
    }

    const sortedBids = Array.from(bidMap.entries())
      .map(([price, d]) => ({ price, ...d }))
      .filter(b => Math.round(b.openQty) > 0)
      .sort((a, b) => b.price - a.price);

    const sortedOffers = Array.from(offerMap.entries())
      .map(([price, d]) => ({ price, ...d }))
      .filter(o => Math.round(o.openQty) > 0)
      .sort((a, b) => a.price - b.price);

    const combined: OrderBookRow[] = [];
    for (let i = 0; i < 14; i++) {
      const bid   = sortedBids[i];
      const offer = sortedOffers[i];
      if (!bid && !offer) break;

      const bc = bid   ? priceChanges.get(`B_${bid.price}`)   : undefined;
      const oc = offer ? priceChanges.get(`S_${offer.price}`) : undefined;
      
      const bidLot = bid ? Math.round(bid.openQty) : 0;
      const offerLot = offer ? Math.round(offer.openQty) : 0;
      const bidFreq = bid ? (bidFreqMap.get(bid.price) ?? 0) : 0;
      const offerFreq = offer ? (offerFreqMap.get(offer.price) ?? 0) : 0;

      combined.push({
        bidFreq:     bidFreq,
        bidBroker:   bc?.broker  ?? '',
        bidDelta:    bc?.delta   ?? 0,
        bidLot:      bidLot,
        bidPrice:    bid   ? bid.price                 : 0,
        offerPrice:  offer ? offer.price               : 0,
        offerLot:    offerLot,
        offerDelta:  oc?.delta   ?? 0,
        offerBroker: oc?.broker  ?? '',
        offerFreq:   offerFreq,
        ghost:       false,
      });
    }

    // Row pairs are already correctly aligned by best bid/offer (index i).
    // No sorting needed here, as it would destroy the alignment.

    // Ensure row 15 is empty to create a gap
    if (combined.length > 0) {
      combined.push({
        bidFreq: 0, bidBroker: '', bidDelta: 0, bidLot: 0, bidPrice: 0,
        offerPrice: 0, offerLot: 0, offerDelta: 0, offerBroker: '', offerFreq: 0,
        ghost: false,
      });
    }

    return combined;
  }, [currentTime, allOrders]);

  const fmtDelta  = (d: number) => d !== 0 ? `${d}` : '';
  const deltaColor = (d: number) => d > 0 ? '#4ade80' : d < 0 ? '#f87171' : 'transparent';

  const getPriceColor = (price: number, defaultColor: string) => {
    if (!price || !prevClose) return defaultColor;
    if (price === prevClose) return 'text-yellow-300';
    if (price > prevClose) return 'text-green-500';
    if (price < prevClose) return 'text-red-500';
    return defaultColor;
  };

  const { isBestFit, handleContextMenu, ContextMenu } = useBestFitContextMenu();

  // Refs for horizontal scroll sync between main table and total row
  const mainScrollRef = useRef<HTMLDivElement>(null);
  const totalDivRef = useRef<HTMLDivElement>(null);
  const syncTotalScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (totalDivRef.current) totalDivRef.current.scrollLeft = e.currentTarget.scrollLeft;
  };

  const colWidths = useMemo(() => {
    let maxBFreq = 4, maxBBr = 3, maxBDel = 3, maxBLot = 4, maxBid = 5, maxOff = 5, maxOLot = 4, maxODel = 3, maxOBr = 3, maxOFreq = 4;
    if (isBestFit) {
      for (const r of rows) {
        maxBFreq = Math.max(maxBFreq, String(r.bidFreq ? r.bidFreq.toLocaleString() : '').length);
        maxBBr = Math.max(maxBBr, String(r.bidBroker).length);
        maxBDel = Math.max(maxBDel, String(fmtDelta(r.bidDelta)).length);
        maxBLot = Math.max(maxBLot, String(r.bidLot ? r.bidLot.toLocaleString() : '').length);
        maxBid = Math.max(maxBid, String(r.bidPrice ? r.bidPrice.toLocaleString() : '').length);
        maxOff = Math.max(maxOff, String(r.offerPrice ? r.offerPrice.toLocaleString() : '').length);
        maxOLot = Math.max(maxOLot, String(r.offerLot ? r.offerLot.toLocaleString() : '').length);
        maxODel = Math.max(maxODel, String(fmtDelta(r.offerDelta)).length);
        maxOBr = Math.max(maxOBr, String(r.offerBroker).length);
        maxOFreq = Math.max(maxOFreq, String(r.offerFreq ? r.offerFreq.toLocaleString() : '').length);
      }
      
      const active = rows.filter(r => !r.ghost);
      const totalBFreq = active.reduce((s, r) => s + r.bidFreq, 0);
      const totalBLot  = active.reduce((s, r) => s + r.bidLot,  0);
      const totalOLot  = active.reduce((s, r) => s + r.offerLot, 0);
      const totalOFreq = active.reduce((s, r) => s + r.offerFreq, 0);
      
      maxBFreq = Math.max(maxBFreq, String(totalBFreq ? totalBFreq.toLocaleString() : '').length);
      maxBLot = Math.max(maxBLot, String(totalBLot ? totalBLot.toLocaleString() : '').length);
      maxOLot = Math.max(maxOLot, String(totalOLot ? totalOLot.toLocaleString() : '').length);
      maxOFreq = Math.max(maxOFreq, String(totalOFreq ? totalOFreq.toLocaleString() : '').length);
    }
    const px = (n: number) => isBestFit ? `${Math.ceil(n * 10.0) + 24}px` : undefined;
    return {
      bFreq: px(maxBFreq), bBr: px(maxBBr), bDel: px(maxBDel), bLot: px(maxBLot), bid: px(maxBid),
      off: px(maxOff), oLot: px(maxOLot), oDel: px(maxODel), oBr: px(maxOBr), oFreq: px(maxOFreq),
    };
  }, [rows, isBestFit]);

  return (
    <div className="bg-black flex flex-col h-full relative">
      <ContextMenu />
      
      <div
        ref={mainScrollRef}
        className={`flex-1 overflow-y-auto ${isBestFit ? 'overflow-x-auto' : 'overflow-x-hidden'}`}
        onScroll={syncTotalScroll}
        style={{ willChange: 'transform' }}
      >
        <table className={`${isBestFit ? '' : 'w-full table-fixed'} text-[14.5px] tabular-nums`}>
          <thead className="sticky top-0 bg-gray-900 text-gray-400 font-bold z-10" style={{ transform: 'translateZ(0)' }} onContextMenu={handleContextMenu}>
            <tr>
              <th className={`px-0 py-0.5 text-center border-b border-gray-700 ${!isBestFit ? 'w-[9%]' : ''}`} style={isBestFit ? {width: colWidths.bFreq} : undefined}>Freq</th>
              {showBroker && <th className={`px-0 py-0.5 border-b border-gray-700 ${!isBestFit ? 'w-[9%]' : ''}`} style={isBestFit ? {width: colWidths.bBr} : undefined} />}
              <th className={`px-0 py-0.5 border-b border-gray-700 ${!isBestFit ? 'w-[7%]' : ''}`} style={isBestFit ? {width: colWidths.bDel} : undefined} />
              <th className={`px-0 py-0.5 text-center border-b border-gray-700 ${!isBestFit ? 'w-[11%]' : ''}`} style={isBestFit ? {width: colWidths.bLot} : undefined}>BLot</th>
              <th className={`px-0 py-0.5 text-center border-b border-gray-700 bg-slate-800/70 ${!isBestFit ? 'w-[11%]' : ''}`} style={isBestFit ? {width: colWidths.bid} : undefined}>Bid</th>
              <th className={`px-0 py-0.5 text-center border-b border-gray-700 bg-slate-800/70 ${!isBestFit ? 'w-[11%]' : ''}`} style={isBestFit ? {width: colWidths.off} : undefined}>Offer</th>
              <th className={`px-0 py-0.5 text-center border-b border-gray-700 ${!isBestFit ? 'w-[11%]' : ''}`} style={isBestFit ? {width: colWidths.oLot} : undefined}>SLot</th>
              <th className={`px-0 py-0.5 border-b border-gray-700 ${!isBestFit ? 'w-[7%]' : ''}`} style={isBestFit ? {width: colWidths.oDel} : undefined} />
              {showBroker && <th className={`px-0 py-0.5 border-b border-gray-700 ${!isBestFit ? 'w-[9%]' : ''}`} style={isBestFit ? {width: colWidths.oBr} : undefined} />}
              <th className={`px-0 py-0.5 text-center border-b border-gray-700 ${!isBestFit ? 'w-[9%]' : ''}`} style={isBestFit ? {width: colWidths.oFreq} : undefined}>Freq</th>
            </tr>
          </thead>
          <tbody>
            {false ? (
              Array.from({ length: 14 }).map((_, idx) => (
                <tr key={idx} className="h-[22.5px] border-b border-gray-900/10">
                  <td className="px-1 py-0.5" style={isBestFit ? {width: colWidths.bFreq} : undefined}>
                    <div className="h-3 bg-gray-800/80 rounded animate-pulse w-3/4 ml-auto" />
                  </td>
                  {showBroker && (
                    <td className="px-1 py-0.5" style={isBestFit ? {width: colWidths.bBr} : undefined}>
                      <div className="h-3 bg-gray-800/80 rounded animate-pulse w-1/2 ml-auto" />
                    </td>
                  )}
                  <td className="px-1 py-0.5" style={isBestFit ? {width: colWidths.bDel} : undefined}>
                    <div className="h-3 bg-gray-800/80 rounded animate-pulse w-5/6 ml-auto" />
                  </td>
                  <td className="px-1 py-0.5" style={isBestFit ? {width: colWidths.bLot} : undefined}>
                    <div className="h-3 bg-gray-800/80 rounded animate-pulse w-full ml-auto" />
                  </td>
                  <td className="px-1 py-0.5 bg-slate-900/40" style={isBestFit ? {width: colWidths.bid} : undefined}>
                    <div className="h-3 bg-gray-700/80 rounded animate-pulse w-4/5 ml-auto" />
                  </td>
                  <td className="px-1 py-0.5 bg-slate-900/40" style={isBestFit ? {width: colWidths.off} : undefined}>
                    <div className="h-3 bg-gray-700/80 rounded animate-pulse w-4/5 mr-auto" />
                  </td>
                  <td className="px-1 py-0.5" style={isBestFit ? {width: colWidths.oLot} : undefined}>
                    <div className="h-3 bg-gray-700/80 rounded animate-pulse w-full ml-auto" />
                  </td>
                  <td className="px-1 py-0.5" style={isBestFit ? {width: colWidths.oDel} : undefined}>
                    <div className="h-3 bg-gray-800/80 rounded animate-pulse w-5/6 mr-auto" />
                  </td>
                  {showBroker && (
                    <td className="px-1 py-0.5" style={isBestFit ? {width: colWidths.oBr} : undefined}>
                      <div className="h-3 bg-gray-800/80 rounded animate-pulse w-1/2 mr-auto" />
                    </td>
                  )}
                  <td className="px-1 py-0.5" style={isBestFit ? {width: colWidths.oFreq} : undefined}>
                    <div className="h-3 bg-gray-800/80 rounded animate-pulse w-3/4 ml-auto" />
                  </td>
                </tr>
              ))
            ) : (
              rows.map((row, idx) => {
                const isLastRow = idx === rows.length - 1;
                return (
                <tr key={idx} className={`${row.ghost ? 'opacity-40' : 'hover:bg-gray-900'} ${isLastRow ? 'h-[12px]' : 'h-[22.5px]'}`}>
                  <td className="px-0 py-0.5 text-right pr-1 text-gray-400" style={isBestFit ? {width: colWidths.bFreq} : undefined}>{row.bidFreq ? row.bidFreq.toLocaleString() : ''}</td>
                  {showBroker && <td className={`px-0 py-0.5 text-right pr-1 text-[12.5px] ${getBrokerColorClass(row.bidBroker)}`} style={isBestFit ? {width: colWidths.bBr} : undefined}>{row.bidBroker}</td>}
                  <td className="px-0 py-0.5 text-right pr-1 text-[12.5px]" style={isBestFit ? {color: deltaColor(row.bidDelta), width: colWidths.bDel} : { color: deltaColor(row.bidDelta) }}>{fmtDelta(row.bidDelta)}</td>
                  <td className={`px-0 py-0.5 text-right pr-[3px] text-gray-300 ${onBidLotClick && row.bidPrice ? 'cursor-pointer hover:bg-gray-800' : ''}`} style={isBestFit ? {width: colWidths.bLot} : undefined} onClick={() => { if (onBidLotClick && row.bidPrice) onBidLotClick(row.bidPrice); }}>{row.bidLot ? row.bidLot.toLocaleString() : ''}</td>
                  <td className={`px-0 py-0.5 text-right pr-2 ${row.bidPrice ? 'bg-slate-800/70' : 'bg-black'} ${getPriceColor(row.bidPrice, 'text-green-500')}`} style={isBestFit ? {width: colWidths.bid} : undefined}>{row.bidPrice ? row.bidPrice.toLocaleString() : ''}</td>
                  <td className={`px-0 py-0.5 text-left pl-2 ${row.offerPrice ? 'bg-slate-800/70' : 'bg-black'} ${getPriceColor(row.offerPrice, 'text-red-500')}`} style={isBestFit ? {width: colWidths.off} : undefined}>{row.offerPrice ? row.offerPrice.toLocaleString() : ''}</td>
                  <td className={`px-0 py-0.5 text-right pr-[3px] text-gray-300 ${onOfferLotClick && row.offerPrice ? 'cursor-pointer hover:bg-gray-800' : ''}`} style={isBestFit ? {width: colWidths.oLot} : undefined} onClick={() => { if (onOfferLotClick && row.offerPrice) onOfferLotClick(row.offerPrice); }}>{row.offerLot ? row.offerLot.toLocaleString() : ''}</td>
                  <td className="px-0 py-0.5 text-left pl-1 text-[12.5px]" style={isBestFit ? {color: deltaColor(row.offerDelta), width: colWidths.oDel} : { color: deltaColor(row.offerDelta) }}>{fmtDelta(row.offerDelta)}</td>
                  {showBroker && <td className={`px-0 py-0.5 text-left pl-1 text-[12.5px] ${getBrokerColorClass(row.offerBroker)}`} style={isBestFit ? {width: colWidths.oBr} : undefined}>{row.offerBroker}</td>}
                  <td className="px-0 py-0.5 text-right pr-[5px] text-gray-400" style={isBestFit ? {width: colWidths.oFreq} : undefined}>{row.offerFreq ? row.offerFreq.toLocaleString() : ''}</td>
                </tr>
                )})
            )}
          </tbody>
        </table>
      </div>
      {/* Total row — always pinned to the bottom of the panel (shrink-0).
          In best-fit mode, scrollLeft is synced from the main div via syncTotalScroll. */}
      {isFullDataLoaded && rows.length > 0 && (() => {
        const active = rows.filter(r => !r.ghost);
        const totalBFreq = active.reduce((s, r) => s + r.bidFreq, 0);
        const totalBLot  = active.reduce((s, r) => s + r.bidLot,  0);
        const totalOLot  = active.reduce((s, r) => s + r.offerLot, 0);
        const totalOFreq = active.reduce((s, r) => s + r.offerFreq, 0);
        return (
          <div
            ref={totalDivRef}
            className="shrink-0 overflow-x-hidden border-t border-gray-700 bg-black"
          >
            <table className={`${isBestFit ? '' : 'w-full table-fixed'} text-[14.5px] tabular-nums font-bold`}>
              <tbody>
                <tr className="text-white h-[24px]">
                  <td className={`px-0 py-0.5 text-right pr-1 text-gray-300 ${!isBestFit ? 'w-[9%]' : ''}`} style={isBestFit ? {width: colWidths.bFreq} : undefined}>{totalBFreq ? totalBFreq.toLocaleString() : ''}</td>
                  {showBroker && <td className={!isBestFit ? 'w-[9%]' : ''} style={isBestFit ? {width: colWidths.bBr} : undefined} />}
                  <td className={!isBestFit ? 'w-[7%]' : ''} style={isBestFit ? {width: colWidths.bDel} : undefined} />
                  <td className={`px-0 py-0.5 text-right text-gray-200 ${!isBestFit ? 'w-[11%]' : ''}`} style={isBestFit ? {width: colWidths.bLot} : undefined}>{totalBLot ? totalBLot.toLocaleString() : ''}</td>
                  <td className={`bg-black border-t border-gray-700 ${!isBestFit ? 'w-[11%]' : ''}`} style={isBestFit ? {width: colWidths.bid} : undefined} />
                  <td className={`bg-black border-t border-gray-700 ${!isBestFit ? 'w-[11%]' : ''}`} style={isBestFit ? {width: colWidths.off} : undefined} />
                  <td className={`px-0 py-0.5 text-right text-gray-200 ${!isBestFit ? 'w-[11%]' : ''}`} style={isBestFit ? {width: colWidths.oLot} : undefined}>{totalOLot ? totalOLot.toLocaleString() : ''}</td>
                  <td className={!isBestFit ? 'w-[7%]' : ''} style={isBestFit ? {width: colWidths.oDel} : undefined} />
                  {showBroker && <td className={!isBestFit ? 'w-[9%]' : ''} style={isBestFit ? {width: colWidths.oBr} : undefined} />}
                  <td className={`px-0 py-0.5 text-right pr-[5px] text-gray-300 ${!isBestFit ? 'w-[9%]' : ''}`} style={isBestFit ? {width: colWidths.oFreq} : undefined}>{totalOFreq ? totalOFreq.toLocaleString() : ''}</td>
                </tr>
              </tbody>
            </table>
          </div>
        );
      })()}
    </div>
  );
}

